import { useQuery } from '@tanstack/react-query'
import { KeyRound, Lock, LogIn, Mail, ShieldCheck } from 'lucide-react'
import QRCode from 'qrcode'
import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Button, Card, Spinner } from '@/components/ui/primitives'
import { Field, TextInput } from '@/components/ui/inputs'
import { useAuth } from '@/lib/auth'
import { api, ApiError } from '@/lib/http'

/** ログイン前の画面の枠。 */
function AuthFrame({ title, subtitle, children }: { title: string; subtitle?: ReactNode; children: ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <p className="eyebrow">Ad Insights Lab</p>
          <h1 className="h-page mt-1">{title}</h1>
          {subtitle ? <p className="mt-2 text-xs leading-relaxed text-ink-3">{subtitle}</p> : null}
        </div>
        <Card className="p-5">{children}</Card>
      </div>
    </div>
  )
}

function ErrorLine({ error }: { error: string | null }) {
  if (!error) return null
  return (
    <p role="alert" className="rounded-lg bg-negative/10 px-3 py-2 text-xs leading-relaxed text-negative">
      {error}
    </p>
  )
}

const message = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : '問題が起きました。時間をおいてやり直してください')

/* ── ログイン ─────────────────────────────────────────────── */

export function LoginPage() {
  const { refresh } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const r = await api.post<{ mfa: 'verify' | 'setup' | 'ok' }>('/auth/login', { email, password })
      await refresh()
      navigate(r.mfa === 'ok' ? '/' : '/mfa', { replace: true })
    } catch (err) {
      setError(message(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthFrame title="ログイン" subtitle="発行されたアカウントでログインしてください">
      <form onSubmit={submit} className="space-y-4">
        <Field label="メールアドレス">
          <TextInput type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
        </Field>
        <Field label="パスワード">
          <TextInput type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <ErrorLine error={error} />
        <Button type="submit" variant="primary" block loading={busy} icon={<LogIn className="h-4 w-4" />}>
          ログイン
        </Button>
        <p className="text-center text-2xs text-ink-3">
          <Link to="/forgot" className="text-accent underline-offset-2 hover:underline">
            パスワードを忘れた時
          </Link>
        </p>
      </form>
    </AuthFrame>
  )
}

/* ── パスワードを忘れた時 ───────────────────────────────────── */

export function ForgotPage() {
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api.post('/auth/forgot', { email })
      setSent(true)
    } catch (err) {
      setError(message(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthFrame title="パスワードの再設定" subtitle="登録のメールアドレスに、再設定のリンクを送ります（1時間有効）">
      {sent ? (
        <div className="space-y-3 text-sm leading-relaxed text-ink-2">
          <p>登録があれば、再設定のリンクを送りました。メールが届かない時は、契約の管理者か運営に再設定を頼んでください。</p>
          <Link to="/login" className="text-xs text-accent underline-offset-2 hover:underline">
            ログインへ戻る
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Field label="メールアドレス">
            <TextInput type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
          </Field>
          <ErrorLine error={error} />
          <Button type="submit" variant="primary" block loading={busy} icon={<Mail className="h-4 w-4" />}>
            リンクを送る
          </Button>
          <p className="text-center text-2xs">
            <Link to="/login" className="text-accent underline-offset-2 hover:underline">
              ログインへ戻る
            </Link>
          </p>
        </form>
      )}
    </AuthFrame>
  )
}

/* ── 招待・再設定のリンク（パスワードを決める） ─────────────── */

export function TokenPage({ kind }: { kind: 'invite' | 'reset' }) {
  const { token = '' } = useParams()
  const navigate = useNavigate()
  const peek = useQuery({
    queryKey: ['token', kind, token],
    queryFn: ({ signal }) => api.get<{ email: string; name: string }>(`/auth/token/${kind}/${encodeURIComponent(token)}`, undefined, signal),
    retry: false,
  })
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  useEffect(() => {
    if (peek.data?.name) setName(peek.data.name)
  }, [peek.data?.name])

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (password !== confirm) {
      setError('確認のパスワードが一致しません')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await api.post(`/auth/token/${kind}/${encodeURIComponent(token)}`, { password, ...(kind === 'invite' ? { name } : {}) })
      setDone(true)
    } catch (err) {
      setError(message(err))
    } finally {
      setBusy(false)
    }
  }

  const title = kind === 'invite' ? 'アカウントの登録' : 'パスワードの再設定'
  if (peek.isLoading) {
    return (
      <AuthFrame title={title}>
        <div className="flex justify-center py-6">
          <Spinner className="h-5 w-5" />
        </div>
      </AuthFrame>
    )
  }
  if (peek.isError) {
    return (
      <AuthFrame title={title}>
        <div className="space-y-3 text-sm leading-relaxed text-ink-2">
          <p>{message(peek.error)}</p>
          <p className="text-xs text-ink-3">招待のリンクは7日、再設定のリンクは1時間で切れます。管理者に出し直しを頼んでください。</p>
          <Link to="/login" className="text-xs text-accent underline-offset-2 hover:underline">
            ログインへ
          </Link>
        </div>
      </AuthFrame>
    )
  }
  if (done) {
    return (
      <AuthFrame title={title}>
        <div className="space-y-4 text-sm leading-relaxed text-ink-2">
          <p>パスワードを決めました。ログインしてください。</p>
          <Button variant="primary" block onClick={() => navigate('/login', { replace: true })}>
            ログインへ
          </Button>
        </div>
      </AuthFrame>
    )
  }
  return (
    <AuthFrame title={title} subtitle={peek.data?.email}>
      <form onSubmit={submit} className="space-y-4">
        {kind === 'invite' ? (
          <Field label="お名前" hint="画面と操作の記録に出ます">
            <TextInput required maxLength={80} value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
        ) : null}
        <Field label="新しいパスワード" hint="10文字以上。メールアドレスや、よくある並びは使えません">
          <TextInput type="password" autoComplete="new-password" required minLength={10} value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <Field label="もう一度">
          <TextInput type="password" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </Field>
        <ErrorLine error={error} />
        <Button type="submit" variant="primary" block loading={busy} icon={<KeyRound className="h-4 w-4" />}>
          決める
        </Button>
      </form>
    </AuthFrame>
  )
}

/* ── 二段階認証（登録と確認） ──────────────────────────────── */

export function MfaPage({ voluntary = false }: { voluntary?: boolean }) {
  const { me, refresh, logout } = useAuth()
  const navigate = useNavigate()
  // voluntary：ログイン済みの人が自分の設定から登録する
  const mode = voluntary ? (me?.user?.totpEnabled ? 'ok' : 'setup') : me?.mfa
  const [setup, setSetup] = useState<{ uri: string; secret: string; qr: string } | null>(null)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [skipping, setSkipping] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (mode === 'ok') navigate(voluntary ? '/account' : '/', { replace: true })
  }, [mode, navigate, voluntary])

  useEffect(() => {
    if (mode !== 'setup' || setup) return
    api
      .post<{ uri: string; secret: string }>('/auth/mfa/setup')
      .then(async (r) => setSetup({ ...r, qr: await QRCode.toDataURL(r.uri, { margin: 1, width: 220 }) }))
      .catch((e) => setError(message(e)))
  }, [mode, setup])

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api.post(mode === 'setup' ? '/auth/mfa/enable' : '/auth/mfa/verify', { code: code.replace(/\s/g, '') })
      await refresh()
      navigate(voluntary ? '/account' : '/', { replace: true })
    } catch (err) {
      setError(message(err))
      setCode('')
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthFrame
      title={mode === 'setup' ? '二段階認証の登録' : '確認コード'}
      subtitle={
        mode === 'setup'
          ? me?.user?.isOperator
            ? '運営のアカウントはすべての契約を扱えるため、登録をおすすめします。認証アプリ（Google Authenticator など）で読み取ってください'
            : '認証アプリ（Google Authenticator など）で読み取ってください'
          : '認証アプリに出ている6桁を入れてください'
      }
    >
      <form onSubmit={submit} className="space-y-4">
        {mode === 'setup' ? (
          setup ? (
            <div className="space-y-2 text-center">
              <img src={setup.qr} alt="認証アプリで読み取るQRコード" className="mx-auto h-44 w-44 rounded-lg border border-line bg-white p-1" />
              <details className="text-left text-2xs text-ink-3">
                <summary className="cursor-pointer">読み取れない時（手で入れる鍵）</summary>
                <code className="mt-1 block break-all rounded bg-surface-2 p-2 font-mono text-ink-2">{setup.secret}</code>
              </details>
            </div>
          ) : (
            <div className="flex justify-center py-6">
              <Spinner className="h-5 w-5" />
            </div>
          )
        ) : (
          <div className="flex justify-center text-accent">
            <ShieldCheck className="h-10 w-10" aria-hidden />
          </div>
        )}
        <Field label="6桁の確認コード">
          <TextInput inputMode="numeric" autoComplete="one-time-code" pattern="[0-9 ]{6,7}" required value={code} onChange={(e) => setCode(e.target.value)} autoFocus className="text-center font-mono text-lg tracking-[0.3em]" />
        </Field>
        <ErrorLine error={error} />
        <Button type="submit" variant="primary" block loading={busy} icon={<Lock className="h-4 w-4" />}>
          {mode === 'setup' ? '登録する' : '確かめる'}
        </Button>
        {mode === 'setup' && !voluntary ? (
          <Button
            variant="ghost"
            block
            loading={skipping}
            onClick={async () => {
              setSkipping(true)
              try {
                await api.post('/auth/mfa/skip')
                await refresh()
                navigate('/', { replace: true })
              } catch (err) {
                setError(message(err))
              } finally {
                setSkipping(false)
              }
            }}
          >
            スキップして使い始める（あとで「自分の設定」から登録できます）
          </Button>
        ) : null}
        <p className="text-center text-2xs">
          {voluntary ? (
            <button type="button" className="text-ink-3 underline-offset-2 hover:underline" onClick={() => navigate('/account')}>
              やめて戻る
            </button>
          ) : (
            <button type="button" className="text-ink-3 underline-offset-2 hover:underline" onClick={() => void logout()}>
              別のアカウントでログインする
            </button>
          )}
        </p>
      </form>
    </AuthFrame>
  )
}
